import {ApiError} from '~/lib/api/errors';
import {mintViewerSession} from '~/lib/api/session';
import {getRefreshTokenForOrganization} from './refresh-token-storage';
import {
  clearViewerSessionIfCurrent,
  getViewerSession,
  isViewerSessionExpired,
  storeViewerSession,
} from './viewer-session-storage';
import type {StoredViewerSession} from './viewer-session-storage';

// Minting is REST, so Apollo's request deduplication doesn't cover it: without
// this, concurrent callers would each mint a session. Keyed by organization,
// and covering re-mints as well as first mints.
const pendingMints = new Map<string, Promise<StoredViewerSession | null>>();

async function mint(
  organizationIdentifier: string,
): Promise<StoredViewerSession | null> {
  const refreshToken = getRefreshTokenForOrganization(organizationIdentifier);
  if (!refreshToken) {
    return null;
  }

  try {
    const minted = await mintViewerSession(
      organizationIdentifier,
      refreshToken,
    );
    const session: StoredViewerSession = {
      token: minted.token,
      expiresAt: minted.expiresAt,
      organizationIdentifier,
    };
    storeViewerSession(session);
    return session;
  } catch (err) {
    // A viewer with no grant is an expected state, so an API rejection degrades
    // to null. Anything else is a bug and should surface.
    if (err instanceof ApiError) {
      return null;
    }
    throw err;
  }
}

// Null means fail closed: every caller degrades rather than surfacing an error.
function mintOnce(
  organizationIdentifier: string,
): Promise<StoredViewerSession | null> {
  const pending = pendingMints.get(organizationIdentifier);
  if (pending) {
    return pending;
  }

  const inFlight = mint(organizationIdentifier).finally(() => {
    pendingMints.delete(organizationIdentifier);
  });

  pendingMints.set(organizationIdentifier, inFlight);
  return inFlight;
}

export async function resolveViewerSession(
  organizationIdentifier: string,
): Promise<StoredViewerSession | null> {
  const stored = getViewerSession();
  const storedMatches =
    stored?.organizationIdentifier === organizationIdentifier;

  if (stored && storedMatches && !isViewerSessionExpired(stored)) {
    return stored;
  }

  const minted = await mintOnce(organizationIdentifier);

  if (!minted && stored && storedMatches) {
    clearViewerSessionIfCurrent(stored.token);
  }
  return minted;
}
